import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import TutorialModal from './TutorialModal';
import DailyChallengeCard from './DailyChallengeCard';
import TournamentMatchCard from './TournamentMatchCard';
import { getAuthUrl } from '../utils/authUrl';
import './MainScreen.css';

const MainScreen = ({ onStartGame, onStartDaily, user, onShowMultiplayer, onShowPublicProfile }) => {
  const { t } = useTranslation();
  const [difficulty, setDifficulty] = useState(null);
  const [gamemode, setGamemode] = useState(null);
  const [gamemodes, setGamemodes] = useState([]);
  const [categories, setCategories] = useState([]);
  const [activeCategory, setActiveCategory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showTutorial, setShowTutorial] = useState(false);
  const [activeMode, setActiveMode] = useState('challenge');
  const [pendingMatches, setPendingMatches] = useState([]);

  // Fetch gamemodes and categories from the backend
  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch gamemodes
        const gamemodesResponse = await fetch('/api/gamemodes');
        if (!gamemodesResponse.ok) {
          throw new Error('Failed to fetch gamemodes');
        }
        const gamemodesData = await gamemodesResponse.json();
        setGamemodes(gamemodesData);

        // Fetch categories
        const categoriesResponse = await fetch('/api/gamemode-categories');
        if (!categoriesResponse.ok) {
          throw new Error('Failed to fetch categories');
        }
        const categoriesData = await categoriesResponse.json();
        setCategories(categoriesData);

        // Set default active category to the first one
        if (categoriesData.length > 0) {
          setActiveCategory(categoriesData[0]);
        }

        // Set default gamemode to the first one in the active category
        if (gamemodesData.length > 0) {
          const firstGamemodeInCategory = gamemodesData.find(
            mode => mode.category === (categoriesData.length > 0 ? categoriesData[0] : null)
          );

          let selectedGamemode;
          if (firstGamemodeInCategory) {
            setGamemode(firstGamemodeInCategory.id);
            selectedGamemode = firstGamemodeInCategory;
          } else {
            setGamemode(gamemodesData[0].id);
            selectedGamemode = gamemodesData[0];
          }

          // Set initial difficulty based on the selected gamemode
          if (selectedGamemode && selectedGamemode.difficulty) {
            setDifficulty(selectedGamemode.difficulty);
          } else {
            setDifficulty('medium');
          }
        }
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Auto-switch to normal if daily challenge already completed
  useEffect(() => {
    if (!user) return;
    const checkDaily = async () => {
      try {
        const res = await fetch('/api/challenges/daily/status', { credentials: 'include' });
        if (res.ok) {
          const data = await res.json();
          if (data.completed) {
            setActiveMode('normal');
          }
        }
      } catch {
        // Silently fail
      }
    };
    checkDaily();
  }, [user]);

  // Fetch pending tournament matches
  useEffect(() => {
    if (!user) {
      setPendingMatches([]);
      return;
    }
    const fetchPendingMatches = async () => {
      try {
        const response = await fetch('/api/integration/my-pending-matches', {
          credentials: 'include'
        });
        if (response.ok) {
          const data = await response.json();
          setPendingMatches(data);
        }
      } catch (err) {
        // Silently fail - tournament matches are optional
      }
    };
    fetchPendingMatches();
  }, [user]);

  const handleJoinTournamentMatch = (lobbyCode) => {
    if (onShowMultiplayer) {
      // Store the code so the multiplayer component can auto-join
      sessionStorage.setItem('tournament_lobby_code', lobbyCode);
      onShowMultiplayer();
    }
  };

  const handleStartGame = () => {
    if (gamemodes.length > 0 && gamemode) {
      onStartGame(difficulty, gamemode);
    } else {
      onStartGame(difficulty);
    }
  };

  const handleGamemodeChange = (e) => {
    const selectedGamemodeId = parseInt(e.target.value);
    setGamemode(selectedGamemodeId);

    const selectedGamemode = gamemodes.find(mode => mode.id === selectedGamemodeId);
    if (selectedGamemode && selectedGamemode.difficulty) {
      setDifficulty(selectedGamemode.difficulty);
    } else {
      setDifficulty('medium');
    }
  };

  const handleCategoryChange = (category) => {
    setActiveCategory(category);

    const firstGamemodeInCategory = gamemodes.find(mode => mode.category === category);
    if (firstGamemodeInCategory) {
      setGamemode(firstGamemodeInCategory.id);

      if (firstGamemodeInCategory.difficulty) {
        setDifficulty(firstGamemodeInCategory.difficulty);
      } else {
        setDifficulty('medium');
      }
    }
  };

  return (
    <div id="main-screen" className="main-screen">
      {user && user.streamer_mode && (
        <div className="streamer-mode-bar">
          <span>{t('userConfig.streamerMode')}</span>
        </div>
      )}

      <section className="hero-section">
        <div className="hero-container">
          {/* Left Column - Logo */}
          <div className="hero-left">
            <img src="/images/logo.png" alt={t('nav.logoAlt', 'OverGuessr - Home')} className="hero-logo" />
          </div>

          {/* Right Column - Game Selection */}
          <div className="hero-right">
            {pendingMatches.length > 0 && (
              <div className="tournament-pending-matches">
                {pendingMatches.map(match => (
                  <TournamentMatchCard
                    key={match.id}
                    match={match}
                    onJoin={handleJoinTournamentMatch}
                  />
                ))}
              </div>
            )}

            <div className="game-panel">
              {!user && (
                <div className="login-reminder">
                  <p>{t('auth.gameLoginReminder')}</p>
                  <a href={getAuthUrl()} className="btn btn-secondary btn-login">
                    {t('auth.loginButton')}
                  </a>
                </div>
              )}

              {loading ? (
                <div className="loading-container">
                  <div className="loading-spinner"></div>
                  <p>{t('game.loadingGameModes')}</p>
                </div>
              ) : (
                <div className="gamemode-selector">
                  <div className="mode-toggle">
                    <button
                      className={`mode-toggle-btn ${activeMode === 'normal' ? 'active' : ''}`}
                      onClick={() => setActiveMode('normal')}
                    >
                      <i className="fas fa-gamepad"></i>
                      <span>{t('game.modeNormal', 'Normal')}</span>
                    </button>
                    <button
                      className={`mode-toggle-btn ${activeMode === 'challenge' ? 'active' : ''}`}
                      onClick={() => setActiveMode('challenge')}
                    >
                      <i className="fas fa-calendar-day"></i>
                      <span>{t('game.modeChallenge', 'Challenge')}</span>
                    </button>
                  </div>

                  {activeMode === 'normal' ? (
                    <>
                      <h3 className="panel-title">{t('game.selectGameMode')}</h3>

                      <div className="category-tabs">
                        <div className="tabs-header">
                          {categories.map(category => (
                            <button
                              key={category}
                              className={`tab-button ${activeCategory === category ? 'active' : ''}`}
                              onClick={() => handleCategoryChange(category)}
                            >
                              {t(`gamemode.${category.toLowerCase()}.${category.toLowerCase()}`, category.toLowerCase())}
                            </button>
                          ))}
                        </div>

                        <div className="tabs-content">
                          {gamemodes
                            .filter(mode => mode.category === activeCategory)
                            .map(mode => (
                              <label
                                key={mode.id}
                                className={`gamemode-option ${gamemode === mode.id ? 'selected' : ''}`}
                              >
                                <input
                                  type="radio"
                                  name="gamemode"
                                  value={mode.id}
                                  checked={gamemode === mode.id}
                                  onChange={handleGamemodeChange}
                                />
                                <span className="option-label">
                                  {t(`gamemode.${mode.category.toLowerCase()}.${mode.code.toLowerCase()}`, mode.name.toLowerCase())}
                                </span>
                                <span className="option-description">
                                  {t(`gamemode.${mode.category.toLowerCase()}.${mode.code.toLowerCase()}-description`, mode.code ? mode.code.toLowerCase() : '')}
                                </span>
                              </label>
                            ))
                          }
                        </div>
                      </div>

                      <div className="panel-actions">
                        <button
                          className="btn btn-tutorial"
                          onClick={() => setShowTutorial(true)}
                          title={t('tutorial.title', 'Tutorial')}
                        >
                          <i className="fas fa-question-circle"></i>
                          <span>{t('tutorial.button', 'How to Play')}</span>
                        </button>

                        <div className="panel-actions-row">
                          <button
                            className="btn btn-multiplayer"
                            onClick={onShowMultiplayer}
                          >
                            <i className="fas fa-users"></i>
                            <span>{t('game.multiplayer', 'Multiplayer')}</span>
                          </button>

                          <button
                            id="start-game-btn"
                            className="btn btn-primary btn-play"
                            onClick={handleStartGame}
                          >
                            <i className="fas fa-play"></i>
                            <span>{t('game.startButton')}</span>
                          </button>
                        </div>
                      </div>
                    </>
                  ) : (
                    user ? (
                      <DailyChallengeCard user={user} onStartDaily={onStartDaily} onSwitchToNormal={() => setActiveMode('normal')} onShowPublicProfile={onShowPublicProfile} />
                    ) : (
                      <div className="challenge-login-required">
                        <i className="fas fa-lock"></i>
                        <p>{t('challenges.loginRequired', 'Log in to access the Daily Challenge')}</p>
                        <a href={getAuthUrl()} className="btn btn-secondary btn-login">
                          {t('auth.loginButton')}
                        </a>
                      </div>
                    )
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Tutorial Modal */}
      {showTutorial && (
        <TutorialModal onClose={() => setShowTutorial(false)} />
      )}
    </div>
  );
};

export default MainScreen;
